import {View} from 'react-native'
import Animated from 'react-native-reanimated'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {HITSLOP_10} from '#/lib/constants'
import {PressableScale} from '#/lib/custom-animations/PressableScale'
import {useHaptics} from '#/lib/haptics'
import {type NavigationProp} from '#/lib/routes/types'
import {useSession} from '#/state/session'
import {useShellLayout} from '#/state/shell/shell-layout'
import {useHomeHeaderTransform} from '#/view/com/util/MainScrollProvider'
import {Logo} from '#/view/icons/Logo'
import {atoms as a, useTheme} from '#/alf'
import {BnuyFactContent, useBnuyFact} from '#/components/BnuyFact'
import {Button, ButtonIcon} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {EyeSlash_Stroke2_Corner0_Rounded as ContentFiltersIcon} from '#/components/icons/EyeSlash'
import {Hashtag_Stroke2_Corner0_Rounded as FeedsIcon} from '#/components/icons/Hashtag'
import * as Layout from '#/components/Layout'
import {Link} from '#/components/Link'
import {
  ContentFiltersFieldset,
  useShowContentFiltersFieldset,
} from '#/components/moderation/ContentFiltersFieldset'
import {useAnalytics} from '#/analytics'
import {DISABLE_LOGO_DEBUG_NAV, IS_DEV, IS_LIQUID_GLASS} from '#/env'

export function HomeHeaderLayoutMobile({
  children,
}: {
  children: React.ReactNode
  tabBarAnchor: React.ReactElement | null | undefined
}) {
  const t = useTheme()
  const {_} = useLingui()
  const ax = useAnalytics()
  const {headerHeight} = useShellLayout()
  const insets = useSafeAreaInsets()
  const headerMinimalShellTransform = useHomeHeaderTransform()
  const {hasSession} = useSession()
  const playHaptic = useHaptics()
  const {navigate} = useNavigation<NavigationProp>()
  const showContentFilters = useShowContentFiltersFieldset()
  const contentFiltersControl = Dialog.useDialogControl()
  const bnuyFactControl = Dialog.useDialogControl()
  const [bnuyFact, rerollBnuyFact] = useBnuyFact()

  return (
    <Animated.View
      style={[
        a.fixed,
        a.z_10,
        t.atoms.bg,
        {
          top: 0,
          left: 0,
          right: 0,
        },
        IS_LIQUID_GLASS && {paddingTop: insets.top},
        headerMinimalShellTransform,
      ]}
      onLayout={e => {
        headerHeight.set(e.nativeEvent.layout.height)
      }}>
      <Layout.Header.Outer noBottomBorder>
        <Layout.Header.Slot>
          <Layout.Header.MenuButton />
        </Layout.Header.Slot>

        <View style={[a.flex_1, a.align_center]}>
          <PressableScale
            targetScale={0.9}
            onPress={() => {
              if (IS_DEV && !DISABLE_LOGO_DEBUG_NAV) {
                navigate('Debug')
              } else {
                playHaptic('Light')
                rerollBnuyFact()
                bnuyFactControl.open()
              }
            }}>
            <Logo width={30} />
          </PressableScale>
        </View>

        {hasSession && showContentFilters && (
          <Layout.Header.Slot>
            <Button
              testID="viewHeaderContentFiltersBtn"
              hitSlop={HITSLOP_10}
              label={_(msg`Quick content filter settings`)}
              size="small"
              variant="ghost"
              color="secondary"
              shape="square"
              onPress={() => {
                ax.metric('nav:click', {
                  item: 'contentFilters',
                  surface: 'topBar',
                })
                contentFiltersControl.open()
              }}
              style={[a.justify_center, a.bg_transparent]}>
              <ButtonIcon icon={ContentFiltersIcon} size="lg" />
            </Button>
          </Layout.Header.Slot>
        )}

        <Layout.Header.Slot>
          {hasSession && (
            <Link
              testID="viewHeaderHomeFeedPrefsBtn"
              to={{screen: 'Feeds'}}
              hitSlop={HITSLOP_10}
              label={_(msg`View your feeds and explore more`)}
              size="small"
              variant="ghost"
              color="secondary"
              shape="square"
              onPress={() => {
                ax.metric('nav:click', {item: 'feeds', surface: 'topBar'})
              }}
              style={[
                a.justify_center,
                {marginRight: -Layout.BUTTON_VISUAL_ALIGNMENT_OFFSET},
                a.bg_transparent,
              ]}>
              <ButtonIcon icon={FeedsIcon} size="lg" />
            </Link>
          )}
        </Layout.Header.Slot>
      </Layout.Header.Outer>
      {children}

      <Dialog.Outer control={bnuyFactControl}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={_(msg`Bnuy fact`)}>
          <BnuyFactContent fact={bnuyFact} />
        </Dialog.ScrollableInner>
      </Dialog.Outer>

      {showContentFilters && (
        <Dialog.Outer control={contentFiltersControl}>
          <Dialog.Handle />
          <Dialog.ScrollableInner label={_(msg`Content filters`)}>
            <ContentFiltersFieldset />
          </Dialog.ScrollableInner>
        </Dialog.Outer>
      )}
    </Animated.View>
  )
}
